import type { ErrorEnvelope } from "@playbookai/types";

/**
 * The words a person sees when a failed request carries no message of its own
 * (a proxy's empty 502, a blank envelope). Screens show `envelope.message` in
 * toasts and alerts, so an empty one showed an empty red toast (PB-5441,
 * Vivek's manual test, 29 Sep 2026).
 */
export function defaultErrorMessage(status: number): string {
  if (status === 502 || status === 503 || status === 504) {
    return "The service is not available right now. Try again in a moment.";
  }
  if (status >= 500) return "Something went wrong on the server. Try again.";
  if (status === 403) return "You do not have permission to do that.";
  if (status === 404) return "That item no longer exists.";
  return "The request failed. Try again.";
}

/**
 * Typed error thrown by playbook-be FE clients on a non-2xx response. Lives in
 * the shared package so `instanceof PlaybookApiError` holds regardless of which
 * module's client (solve / hoshin / …) made the call. `envelope.message` is
 * never empty: a blank one takes {@link defaultErrorMessage}.
 */
export class PlaybookApiError extends Error {
  readonly envelope: ErrorEnvelope;

  constructor(
    readonly status: number,
    envelope: ErrorEnvelope,
  ) {
    const message =
      typeof envelope.message === "string" && envelope.message.trim() !== ""
        ? envelope.message
        : defaultErrorMessage(status);
    super(message);
    this.envelope =
      message === envelope.message ? envelope : { ...envelope, message };
    this.name = "PlaybookApiError";
  }
}
